import { useEffect, useState } from 'react';
import { api } from './api.js';

interface Vial {
  id: string; compound: string; vendorName: string | null; batchRef: string | null;
  labelMgMl: number | null; measuredMgMl: number | null; measuredPurityPct: number | null;
  assayStatus: string; assayLab: string | null; assayDate: string | null;
  deliveredPct: number | null; verdict: string;
}

interface Rubric { question: string; why: string; answer: string; detail: string | null }
interface Vendor {
  id: string; name: string; website: string | null; vialCount: number;
  verifiedCount: number; failedCount: number; rubric: Rubric[]; standing: string;
}

interface Panel { takenAt: string; marker: string; value: number; unit: string | null }
interface Batch {
  vialId: string; batchRef: string | null; vendorName: string | null; compound: string;
  assayStatus: string; labelMgMl: number | null; measuredMgMl: number | null; panels: Panel[];
}

export function Sources(): JSX.Element {
  const [vials, setVials] = useState<Vial[]>([]);
  const [unassayed, setUnassayed] = useState(0);
  const [failed, setFailed] = useState(0);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [note, setNote] = useState('');

  useEffect(() => {
    api<{ vials: Vial[]; unassayed: number; failed: number }>('/provenance/vials')
      .then((r) => { setVials(r.vials); setUnassayed(r.unassayed); setFailed(r.failed); })
      .catch(() => undefined);
    api<{ vendors: Vendor[] }>('/provenance/vendors').then((r) => setVendors(r.vendors)).catch(() => undefined);
    api<{ groups: Batch[]; note: string }>('/provenance/bloods-by-batch')
      .then((r) => { setBatches(r.groups); setNote(r.note); }).catch(() => undefined);
  }, []);

  return (
    <>
      <div className="card">
        <h2>Sources and batches</h2>
        <p className="muted small">What you can verify, and what has never been checked.</p>
        {failed > 0 && <p><span className="chip chip-low">{failed} failed assay</span></p>}
        {unassayed > 0 && (
          <p className="muted small">
            {unassayed} of your vials have never been tested. An unverified vial is an unknown
            quantity of an unknown substance.
          </p>
        )}
        {vials.length === 0 && <p className="muted small">No vials recorded yet.</p>}

        <table className="tbl">
          <tbody>
            {vials.map((v) => (
              <tr key={v.id}>
                <td>
                  {v.compound}
                  <div className="muted small">
                    {[v.vendorName, v.batchRef && `batch ${v.batchRef}`, v.labelMgMl && `labelled ${v.labelMgMl} mg/ml`]
                      .filter(Boolean).join(' · ')}
                  </div>
                </td>
                <td>
                  <span className={`chip ${v.assayStatus === 'verified' ? 'chip-high' : v.assayStatus === 'failed' ? 'chip-low' : 'chip-medium'}`}>
                    {v.assayStatus}
                  </span>
                  {v.deliveredPct != null && <div className="muted small">{v.deliveredPct}% of label</div>}
                </td>
                <td className="muted small">{v.verdict}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {batches.length > 0 && (
        <div className="card">
          <h2>Your bloods, by batch</h2>
          <p className="muted small">{note}</p>
          {batches.map((b) => (
            <div key={b.vialId} style={{ marginTop: 12 }}>
              <strong>{b.batchRef ? `Batch ${b.batchRef}` : b.compound}</strong>
              {b.vendorName && <span className="muted"> · {b.vendorName}</span>}
              <div className="muted small">
                {b.labelMgMl ? `Labelled ${b.labelMgMl} mg/ml` : 'No label recorded'}
                {b.measuredMgMl ? `, measured ${b.measuredMgMl} mg/ml` : ', never measured'}
              </div>
              <table className="rows">
                <tbody>
                  {b.panels.map((p) => (
                    <tr key={`${p.marker}-${p.takenAt}`}>
                      <td>{p.marker.replace(/_/g, ' ')}</td>
                      <td className="dim">{p.value} {p.unit}</td>
                      <td className="dim">{p.takenAt}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {vendors.map((v) => (
        <div className="card" key={v.id}>
          <h2>{v.name}</h2>
          <p>{v.standing}</p>
          {v.rubric.map((q) => (
            <div key={q.question} style={{ marginTop: 10 }}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <strong className="small">{q.question}</strong>
                <span className={`chip ${q.answer === 'yes' ? 'chip-high' : q.answer === 'no' ? 'chip-low' : ''}`}>
                  {q.answer === 'unknown' ? 'not known' : q.answer}
                </span>
              </div>
              <p className="muted small">{q.why}</p>
              {q.detail && <p className="muted small">{q.detail}</p>}
            </div>
          ))}
        </div>
      ))}
    </>
  );
}
